import { useCallback, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorRemixLifecycleObservation } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';

type ProducerAttemptObservation = {
	admissionHook: 'available' | 'unavailable';
	artifactType: 'image' | 'maui' | 'unknown';
	outcome: 'admitted' | 'legacy' | 'rejected' | 'unavailable';
	target: 'present' | 'missing';
};

const toLifecycleObservation = (
	observation: ProducerAttemptObservation,
): Extract<EditorRemixLifecycleObservation, { actionSubjectId: 'producerAttempt' }> => ({
	actionSubjectId: 'producerAttempt',
	producer: 'inline-rovo-edit',
	artifactType: observation.artifactType,
	outcome: observation.outcome,
	reason:
		observation.target === 'present'
			? observation.admissionHook === 'available'
				? 'target-present-admission-available'
				: 'target-present-admission-unavailable'
			: observation.admissionHook === 'available'
				? 'target-missing-admission-available'
				: 'target-missing-admission-unavailable',
});

/**
 * Records the producer-side unavailable path when the treatment-only lifecycle host is absent.
 *
 * The normal lifecycle host owns this observation when it is mounted. This fallback deliberately
 * registers only reporting, never admission, so the producer remains fail-open and its existing
 * submit behavior is unchanged.
 */
export const EditorInlineRovoEditProducerAttemptFallback = ({
	api,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
}): null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const report = useCallback(
		(observation: ProducerAttemptObservation): void => {
			const lifecycleObservation = toLifecycleObservation(observation);
			try {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'observed',
						actionSubject: 'editorRemixLifecycleOwnership',
						actionSubjectId: lifecycleObservation.actionSubjectId,
						source: 'editorRemix',
						tags: ['remix', 'stateRearchitecture'],
						attributes: {
							migrationPhase: 'ownership-soak',
							producer: lifecycleObservation.producer,
							artifactType: lifecycleObservation.artifactType,
							outcome: lifecycleObservation.outcome,
							reason: lifecycleObservation.reason,
						},
					},
				}).fire();
			} catch {
				// Observability must never affect submission behavior.
			}
		},
		[createAnalyticsEvent],
	);

	useEffect(
		() => api?.aiExperience?.actions.registerInlineRovoEditProducerAttemptReporting?.(report),
		[api, report],
	);

	return null;
};
